import { Body, Button, Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TextField } from 'src/components/TextField';
import { GoogleAuthButton } from 'src/legacy/components/UI/Buttons/GoogleAuthButton';
import { ensureError } from 'src/utils/Errors';

import { StudioArrivalContext } from './StudioArrivalContext';
import { StudioPageFrame } from './StudioPageFrame';
import {
  STUDIO_BUTTON_FOCUS_CLASS,
  STUDIO_INPUT_FOCUS_CLASS,
  STUDIO_PRIMARY_BUTTON_CLASS,
} from './studioStyles';
import { STUDIO_ACCOUNT_ALREADY_EXISTS_CODE } from './utils/studioAuth';

interface StudioGetStartedFormValues {
  email: string;
}

export interface StudioGetStartedProps {
  onContinueWithGoogle: () => void;
  onContinueWithEmail: (email: string) => Promise<void>;
  // Arrival context from marketing (prompt wins over template when both set).
  prompt?: string;
  templateName?: string;
  templateDescription?: string;
  // Optional copy overrides so the same input design can back both the signup
  // ("Get started") and login screens. Default to the signup copy.
  title?: string;
  // When omitted, no subtitle is rendered (signup). Login passes its own copy.
  subtitle?: string;
  submitLabel?: string;
  // Cross-navigation footer to switch between the signup and login flows. The
  // link is a full navigation so the destination's `referrer=studio` gate
  // re-evaluates on load. Rendered only when both label and href are provided.
  footerPrompt?: string;
  footerActionLabel?: string;
  footerActionHref?: string;
}

export const StudioGetStarted = ({
  onContinueWithGoogle,
  onContinueWithEmail,
  prompt,
  templateName,
  templateDescription,
  title,
  subtitle,
  submitLabel,
  footerPrompt,
  footerActionLabel,
  footerActionHref,
}: StudioGetStartedProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email(t(($) => $.page_onboarding.studio.email_invalid))
          .required(t(($) => $.page_onboarding.studio.email_required)),
      }),
    [t],
  );

  const hasArrivalContext = Boolean(prompt?.trim() || templateName?.trim());

  return (
    <StudioPageFrame>
      {/*
        Stretch the column so context + form share the same 446px width.
        Only the header (logo/title) is centered inside that column.
      */}
      <div className="mx-auto flex w-full min-w-0 max-w-full flex-col items-stretch gap-10">
        <div className="flex w-full flex-col items-center gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-black">
            <Icon
              icon="AssemblyLogo"
              className="h-[22px] w-[22px] text-white"
            />
          </div>
          <Body className="w-full text-center font-studio text-[30px] leading-[1.25] text-black">
            {title ?? t(($) => $.page_onboarding.studio.title)}
          </Body>
          {subtitle ? (
            <Body
              size="lg"
              className="mb-0 w-full font-studio text-center text-[#5f636a]"
            >
              {subtitle}
            </Body>
          ) : null}
        </div>

        {hasArrivalContext ? (
          <div className="w-full min-w-0 self-stretch">
            <StudioArrivalContext
              prompt={prompt}
              templateName={templateName}
              templateDescription={templateDescription}
            />
          </div>
        ) : null}

        <Formik<StudioGetStartedFormValues>
          initialValues={{ email: '' }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setFieldError, setSubmitting }) => {
            try {
              await onContinueWithEmail(values.email.trim());
            } catch (error) {
              const normalizedError = ensureError(error) as Error & {
                code?: string;
              };
              const message =
                normalizedError.code === STUDIO_ACCOUNT_ALREADY_EXISTS_CODE
                  ? t(($) => $.page_onboarding.studio.account_already_exists)
                  : normalizedError.message;
              setFieldError('email', message);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form className="flex w-full min-w-0 self-stretch flex-col gap-6">
              <GoogleAuthButton
                onClick={onContinueWithGoogle}
                className={clsx(
                  '!w-full h-12 hover:!bg-[#f5f5f0] [&_.MuiTypography-root]:!text-[15px] [&_.MuiTypography-root]:!font-normal',
                  STUDIO_BUTTON_FOCUS_CLASS,
                )}
              />

              <div className="flex w-full items-center gap-4">
                <div className="h-px flex-1 bg-non-hover-border" />
                <span className="font-studio text-[13px] leading-[1.5] text-[#5f636a]">
                  {t(($) => $.page_onboarding.studio.or)}
                </span>
                <div className="h-px flex-1 bg-non-hover-border" />
              </div>

              <div className="flex w-full flex-col gap-2">
                <label
                  htmlFor="studio-get-started-email"
                  className="font-studio text-[13px] leading-[1.5] text-black"
                >
                  {t(($) => $.page_onboarding.studio.email_label)}
                </label>
                <TextField
                  id="studio-get-started-email"
                  sizeVariant="tall"
                  fullWidth
                  type="email"
                  name="email"
                  variant="outlined"
                  className={STUDIO_INPUT_FOCUS_CLASS}
                  placeholder={t(
                    ($) => $.page_onboarding.studio.email_placeholder,
                  )}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="email"
                  error={Boolean(touched.email && errors.email)}
                  // Only render helper text on error so the Figma 24px gap
                  // between input and CTA isn't padded by an empty helper.
                  helperText={
                    touched.email && errors.email ? errors.email : undefined
                  }
                />
              </div>

              <Button
                type="submit"
                id="studioContinueWithEmail"
                className={clsx('h-12 w-full', STUDIO_PRIMARY_BUTTON_CLASS)}
                variant="studio"
                label={
                  submitLabel ??
                  t(($) => $.page_onboarding.studio.continue_with_email)
                }
                disabled={isSubmitting}
                loading={isSubmitting}
              />

              {footerActionLabel && footerActionHref ? (
                <p className="m-0 text-center font-studio text-[13px] leading-[1.5] text-[#5f636a]">
                  {footerPrompt}{' '}
                  <a
                    href={footerActionHref}
                    className="text-black underline-offset-2 hover:underline"
                  >
                    {footerActionLabel}
                  </a>
                </p>
              ) : null}
            </Form>
          )}
        </Formik>
      </div>
    </StudioPageFrame>
  );
};
